import { createSlice } from "@reduxjs/toolkit";
import {
  fetchMedicalInfoByUserId,
  fetchMedicalHistory,
  fetchMedicalInfoById,
  createMedicalInfo,
  updateMedicalInfo,
  updateMedicalInfoById,
  deleteMedicalInfoById,
  deleteAllMedicalInfo,
  // ✅ NEW
  fetchInsuranceProviders,
  fetchUserInsurances,
} from "../thunks/medicalThunks";

// Utils: insert/replace en tête + tri par date desc
const upsertHead = (arr, rec) => {
  if (!rec) return arr;
  const next = Array.isArray(arr) ? arr.slice() : [];
  const idx = next.findIndex(r => r?.id === rec.id);
  if (idx !== -1) next.splice(idx, 1);
  next.unshift(rec);
  return next.sort((a, b) => new Date(b?.addedAt || 0) - new Date(a?.addedAt || 0));
};
const replaceIfExists = (arr, rec) => {
  if (!rec) return arr;
  const next = Array.isArray(arr) ? arr.slice() : [];
  const idx = next.findIndex(r => r?.id === rec.id);
  if (idx !== -1) next[idx] = rec;
  return next.sort((a, b) => new Date(b?.addedAt || 0) - new Date(a?.addedAt || 0));
};

const stringifyError = (value) => {
  if (!value) return 'Unknown error';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.map((item) => stringifyError(item)).join(', ');
  if (typeof value === 'object') {
    const message = value.message || value.error || value.detail || value.errorMessage;
    if (message) return stringifyError(message);
    try {
      return JSON.stringify(value);
    } catch {
      return Object.prototype.toString.call(value);
    }
  }
  return String(value);
};

const getRequestedUserId = (target) =>
  typeof target === "object" && target !== null
    ? target.userId
    : target;

const initialState = {
   byId: {},
  fetchedUserIds: {},  // Track which userIds have been fetched
  // Dernier enregistrement (snapshot actuel)
  data: null,

  // Historique complet (tableau d'enregistrements)
  history: [],
  loadingMap: {},
  // Enregistrement sélectionné (pour édition)
  selectedRecord: null,

  // États de chargement
  loading: false,
  historyLoading: false,

  // Erreurs
  error: null,

  // Flags
  hasFetched: false,
  historyHasFetched: false,

  // ✅ NEW — liste des assureurs + état
  providers: [],
  providersLoading: false,
  providersError: null,
};

const medicalSlice = createSlice({
  name: "medical",
  initialState,
  reducers: {
    resetMedical: () => ({ ...initialState }),

    clearMedicalError: (state) => {
      state.error = null;
    },

    setSelectedRecord: (state, action) => {
      state.selectedRecord = action.payload;
    },

    clearSelectedRecord: (state) => {
      state.selectedRecord = null;
    },
  },

  extraReducers: (builder) => {
    builder
// ========== GET dernier enregistrement ==========
.addCase(fetchMedicalInfoByUserId.pending, (state, action) => {
  const userId = getRequestedUserId(action.meta.arg);
  state.loadingMap[userId] = true;
  state.loading = true;
  state.error = null;
})

.addCase(fetchMedicalInfoByUserId.fulfilled, (state, action) => {
  const userId = getRequestedUserId(action.meta.arg);
  const data   = action.payload;           // ✅ payload IS the record directly

  if (!userId) return;

  state.byId[userId]        = data;        // normalized map
  state.fetchedUserIds[userId] = true;     // ✅ Mark as fetched
  state.data                = data;        // snapshot — now correctly has groupeSanguin etc.
  state.hasFetched          = true;
  state.loadingMap[userId]  = false;
  state.loading             = false;
})

.addCase(fetchMedicalInfoByUserId.rejected, (state, action) => {
  const userId = getRequestedUserId(action.meta.arg);
  state.loadingMap[userId] = false;
  state.fetchedUserIds[userId] = true;     // ✅ Mark as fetched even on error
  state.error      = stringifyError(action.payload || action.error?.message || "Fetch error");
  state.hasFetched = true;
  state.loading    = false;
})
      // ========== GET historique complet ==========
      .addCase(fetchMedicalHistory.pending, (s) => {
        s.historyLoading = true;
        s.error = null;
      })
      .addCase(fetchMedicalHistory.fulfilled, (s, a) => {
        s.historyLoading = false;
        const list = Array.isArray(a.payload) ? a.payload : (a.payload ? [a.payload] : []);
        // tri desc par addedAt (le plus récent en premier)
        s.history = list.slice().sort(
          (x, y) => new Date(y?.addedAt || 0) - new Date(x?.addedAt || 0)
        );
        s.historyHasFetched = true;
      })
      .addCase(fetchMedicalHistory.rejected, (s, a) => {
        s.historyLoading = false;
        s.error = stringifyError(a.payload || a.error?.message || "History fetch error");
        s.historyHasFetched = true;
      })

      // ========== GET enregistrement par ID ==========
      .addCase(fetchMedicalInfoById.pending, (s) => {
        s.loading = true;
        s.error = null;
      })
      .addCase(fetchMedicalInfoById.fulfilled, (s, a) => {
        s.loading = false;
        s.selectedRecord = a.payload || null;
      })
      .addCase(fetchMedicalInfoById.rejected, (s, a) => {
        s.loading = false;
        s.error = stringifyError(a.payload || a.error?.message || "Fetch by id error");
      })

      // ========== CREATE (POST) ==========
      .addCase(createMedicalInfo.pending, (s) => {
        s.loading = true;
        s.error = null;
      })
      .addCase(createMedicalInfo.fulfilled, (s, a) => {
        s.loading = false;
        const rec = a.payload || null;
        const userId = a.meta?.arg?.userId;
        if (userId) {
          s.byId[userId] = rec;
          s.fetchedUserIds[userId] = true;
        }
        s.data = rec;                 // le “dernier” devient ce qu’on vient de créer
        s.history = upsertHead(s.history, rec); // ✅ ajoute en tête (remplace si id identique), garde ordre desc
      })
      .addCase(createMedicalInfo.rejected, (s, a) => {
        s.loading = false;
        s.error = stringifyError(a.payload || a.error?.message || "Create error");
      })

      // ========== UPDATE par userId ==========
      .addCase(updateMedicalInfo.pending, (s) => {
        s.loading = true;
        s.error = null;
      })
      .addCase(updateMedicalInfo.fulfilled, (s, a) => {
        s.loading = false;
        const rec = a.payload || null;
        const userId = a.meta?.arg?.userId;
        if (userId) {
          s.byId[userId] = rec;
          s.fetchedUserIds[userId] = true;
        }
        s.data = rec;
        // ✅ met à jour l'entrée existante si présente dans l'historique (pas de duplication)
        s.history = replaceIfExists(s.history, rec);
      })
      .addCase(updateMedicalInfo.rejected, (s, a) => {
        s.loading = false;
        s.error = stringifyError(a.payload || a.error?.message || "Update error");
      })

      // ========== UPDATE par ID ==========
      .addCase(updateMedicalInfoById.pending, (s) => {
        s.loading = true;
        s.error = null;
      })
      .addCase(updateMedicalInfoById.fulfilled, (s, a) => {
        s.loading = false;
        const rec = a.payload || null;
        s.selectedRecord = rec;

        if (s.data && rec && s.data.id === rec.id) {
          s.data = rec;
        }
        s.history = replaceIfExists(s.history, rec);
      })
      .addCase(updateMedicalInfoById.rejected, (s, a) => {
        s.loading = false;
        s.error = stringifyError(a.payload || a.error?.message || "Update by id error");
      })

      // ========== DELETE par ID ==========
      .addCase(deleteMedicalInfoById.pending, (s) => {
        s.loading = true;
        s.error = null;
      })
      .addCase(deleteMedicalInfoById.fulfilled, (s, a) => {
        s.loading = false;
        const deletedId = a.payload?.id;
        s.history = (s.history || []).filter(r => r?.id !== deletedId);

        if (s.data && s.data.id === deletedId) {
          s.data = s.history[0] || null; // reprend le plus récent restant
        }
        if (s.selectedRecord && s.selectedRecord.id === deletedId) {
          s.selectedRecord = null;
        }
      })
      .addCase(deleteMedicalInfoById.rejected, (s, a) => {
        s.loading = false;
        s.error = stringifyError(a.payload || a.error?.message || "Delete error");
      })

      // ========== DELETE tous les enregistrements ==========
      .addCase(deleteAllMedicalInfo.pending, (s) => {
        s.loading = true;
        s.error = null;
      })
      .addCase(deleteAllMedicalInfo.fulfilled, (s) => {
        s.loading = false;
        s.data = null;
        s.history = [];
        s.selectedRecord = null;
        s.hasFetched = false;
        s.historyHasFetched = false;
      })
      .addCase(deleteAllMedicalInfo.rejected, (s, a) => {
        s.loading = false;
        s.error = stringifyError(a.payload || a.error?.message || "Delete all error");
      })

      // ========== ✅ NEW: Insurance providers ==========
      .addCase(fetchInsuranceProviders.pending, (s) => {
        s.providersLoading = true;
        s.providersError = null;
      })
      .addCase(fetchInsuranceProviders.fulfilled, (s, a) => {
        s.providersLoading = false;
        s.providers = Array.isArray(a.payload) ? a.payload : [];
      })
      .addCase(fetchInsuranceProviders.rejected, (s, a) => {
        s.providersLoading = false;
        s.providersError = stringifyError(a.payload || a.error?.message || "Failed to load providers");
      })

      // ✅ New: User insurances (from medical record)
      .addCase(fetchUserInsurances.pending, (s) => {
        s.loading = true;
        s.error = null;
      })
      .addCase(fetchUserInsurances.fulfilled, (s, a) => {
        s.loading = false;
        const ins = Array.isArray(a.payload) ? a.payload : [];
        if (!s.data) {
          s.data = { insurance: ins };
        } else {
          s.data.insurance = ins;
        }
      })
      .addCase(fetchUserInsurances.rejected, (s, a) => {
        s.loading = false;
        s.error = stringifyError(a.payload || a.error?.message || "Failed to load user insurances");
      });
  },
});

export const {
  resetMedical,
  clearMedicalError,
  setSelectedRecord,
  clearSelectedRecord,
} = medicalSlice.actions;

export default medicalSlice.reducer;
